import React, { useState } from 'react'

export const DEFAULT_PAGE_SIZE = 8

// Page index that survives a browser refresh (F5). Persisted in sessionStorage
// under a caller-supplied scope (e.g. "place-modules-<placeId>") so different
// tables/places/rooms don't clobber each other. Cleared when the tab closes.
export function usePersistedPage(scope: string): [number, (p: number) => void] {
    const storageKey = `dayro_page_${scope}`
    const [page, setPageState] = useState<number>(() => {
        try {
            const raw = sessionStorage.getItem(storageKey)
            const n = raw == null ? 0 : parseInt(raw, 10)
            return Number.isFinite(n) && n >= 0 ? n : 0
        } catch { return 0 }
    })
    const setPage = (p: number) => {
        setPageState(p)
        try { sessionStorage.setItem(storageKey, String(p)) } catch { /* storage blocked — ignore */ }
    }
    return [page, setPage]
}

interface Props {
    page: number
    totalPages: number
    total: number
    pageSize?: number
    onPage: (p: number) => void
}

const PaginationBar = ({ page, totalPages, total, pageSize = DEFAULT_PAGE_SIZE, onPage }: Props) => {
    // Empty result — render nothing (caller already shows an "empty state" row).
    if (total === 0) return null
    const from = page * pageSize + 1
    const to = Math.min((page + 1) * pageSize, total)
    const btn = (label: React.ReactNode, target: number, disabled: boolean, active = false) => (
        <button
            key={`${label}-${target}`}
            onClick={() => !disabled && onPage(target)}
            disabled={disabled}
            style={{
                padding: '4px 10px', fontSize: 13, fontWeight: active ? 600 : 400,
                background: active ? '#111827' : '#fff',
                color: active ? '#fff' : disabled ? '#9CA3AF' : '#374151',
                border: '1px solid #D1D5DB', borderRadius: 4,
                cursor: disabled ? 'not-allowed' : 'pointer', marginRight: 4,
            }}
        >{label}</button>
    )
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 16px', borderTop: '1px solid #E4E7EC', background: '#fff',
            fontSize: 13, color: '#475467',
        }}>
            <span>Showing {from}-{to} of {total}</span>
            {/* Buttons only render when there's more than one page. The count
                line above stays visible regardless, so the user always sees the
                total and never wonders whether pagination is broken. */}
            {totalPages > 1 && (
                <div>
                    {btn('« Prev', page - 1, page === 0)}
                    {Array.from({ length: totalPages }, (_, i) =>
                        btn(String(i + 1), i, false, i === page),
                    )}
                    {btn('Next »', page + 1, page >= totalPages - 1)}
                </div>
            )}
        </div>
    )
}

// Small helper so callers don't repeat the math.
export function paginate<T>(items: T[], page: number, pageSize: number = DEFAULT_PAGE_SIZE) {
    const totalPages = Math.max(1, Math.ceil(items.length / pageSize))
    const safePage = Math.min(page, totalPages - 1)
    const paged = items.slice(safePage * pageSize, (safePage + 1) * pageSize)
    return { paged, totalPages, safePage }
}

export default PaginationBar
